import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import styles from "./resetPassword.module.scss";
import Button from "src/components/button";
import { useDispatch } from "react-redux";
import { resetPassword, validatePasswordReset } from "src/redux/actions/users";
import { useSelector } from "react-redux";
import _ from "lodash";
import cx from "classnames";

function ResetPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { id } = useParams() as { id: string };
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const key = searchParams.get("key") as string;

  const [user, setUser] = useState({
    password: "",
    confirmPassword: "",
  });

  const [error, setError] = useState({
    password: "",
    confirmPassword: "",
  });

  const loading = useSelector((state) =>
    _.get(state, "users.resetPassword.changing"),
  );

  const validationErrorCode = useSelector((state) => 
    _.get(state, "users.validate.error.errors[0].code", ''),
  );

  const resetPasswordErrorCode = useSelector((state) =>
    _.get(state, "users.resetPassword.error.errors[0].code"),
  ) as any;

  const resetPasswordErrorMessage = useSelector((state) =>
    _.get(state, "users.resetPassword.error.errors[0].message"),
  ) as any;

  const passwordChanged = useSelector((state) =>
    _.get(state, "users.resetPassword.changed"),
  );

  const [showPasswordText, setShowPasswordText] = useState(false);
  const [showConfirmPasswordText, setShowConfirmPasswordText] = useState(false);

  useEffect(() => {
    dispatch(validatePasswordReset({ id: id, key: key }));
  }, []);

  useEffect(() => {
    if (resetPasswordErrorCode === 'password.previous.match') {
      setError({
        ...error,
        password: resetPasswordErrorMessage,
      });
    }
  }, [resetPasswordErrorCode]);

  useEffect(() => {
    if (passwordChanged) {
      window.location.href = "/public/login";
    }
  }, [passwordChanged]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUser((prev) => ({ ...prev, [name]: value?.trim() }));
    if (name in error) {
      setError((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (user.password.length < 8) {
      setError((prev) => ({
        ...prev,
        password: "Password should be at least 8 characters",
      }));
      return;
    }
    if (user.password !== user.confirmPassword) {
      setError((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match",
      }));
      return;
    }
    setError({ password: "", confirmPassword: "" });

    dispatch(
      resetPassword({
        id: id,
        passwordData: {
          newPassword1: user.password,
          newPassword2: user.confirmPassword,
          recoveryKey: key,
        },
      }),
    );
  };

  return (
    <div className={styles.resetContainer}>
      <img
        src="/images/Paperflite.png"
        alt="Paperflite Logo"
        className={styles.logo}
      />
      <h1 className={styles.title}>
        {validationErrorCode === 'resource.not_found' ? (
          <div className={styles.linkExpired}>
            Password reset link expired
            <p className={styles.signIn}>
              Try{" "}
              <a href="/forgot-password">
                <span className={styles.signInText}>resetting</span>
              </a>
              again?
            </p>
          </div>
        ) : (
          "Please enter a new password for your account"
        )}
      </h1>
      {validationErrorCode !== 'resource.not_found' && (
        <form
          noValidate
          autoComplete="off"
          className={styles.activateForm}
          onSubmit={handleSubmit}
        >
          <span className={styles.passwordContainer}>
            <input
              className={styles.input}
              type={showPasswordText ? "text" : "password"}
              name="password"
              placeholder="Set password"
              value={user.password}
              autoComplete="new-password"
              onChange={handleChange}
              onPaste={(e) => e.preventDefault()}
            />
            <i
              onClick={() => setShowPasswordText(!showPasswordText)}
              className={cx(
                styles.eyeIcon,
                showPasswordText ? "paperflite-hidden-eye" : "paperflite-eye",
              )}
            ></i>
          </span>

          {error.password && (
            <p className={styles.error}>
              <i className="paperflite-danger"></i>
              <label>{error.password}</label>
            </p>
          )}
          <span className={styles.passwordContainer}>
            <input
              className={styles.input}
              type={showConfirmPasswordText ? "text" : "password"}
              name="confirmPassword"
              placeholder="Confirm password"
              value={user.confirmPassword}
              autoComplete="new-password"
              onChange={handleChange}
              onPaste={(e) => e.preventDefault()}
            />
            <i
              onClick={() =>
                setShowConfirmPasswordText(!showConfirmPasswordText)
              }
              className={cx(
                styles.eyeIcon,
                showConfirmPasswordText
                  ? "paperflite-hidden-eye"
                  : "paperflite-eye",
              )}
            ></i>
          </span>
          {error.confirmPassword && (
            <p className={styles.error}>
              <i className="paperflite-danger"></i>
              <label>{error.confirmPassword}</label>
            </p>
          )}
          <Button
            className={styles.button}
            variant="primary"
            icon="paperflite-right-arrow"
            disabled={loading}
            onClick={handleSubmit}
          >
            {loading ? "Resetting your password" : "Reset your password"}
          </Button>
        </form>
      )}
      <p className={styles.signUp} onClick={() => navigate("/public/login")}>
        Already using Paperflite?
        <span className={styles.signUpText}>Sign in</span>
      </p>
    </div>
  );
}

export default ResetPassword;
